import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Users, Search, LogOut, Settings, UserPlus, Edit,
  X, CheckCircle, XCircle, FolderPlus, Folder, Send,
  Briefcase, GraduationCap, Award, Bell, MessageSquare, FileText,
  Plus, Trash2, Globe, Lock, Upload, MapPin, User, Shield, Key, ChevronLeft, Phone, MessageCircle, Star, MoreHorizontal, Activity, Building2,
  Eye, EyeOff, AlertCircle, Mail
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import LanguageSelector from '../components/LanguageSelector';
import api from '../services/api';
import NotificationBell from '../components/NotificationBell';

import ProfileEditor from '../components/ProfileEditor';
import CandidateProfileView from '../components/CandidateProfileView';
import { useLanguage } from '../context/LanguageContext';
import TeamMembersTab from '../components/TeamMembersTab';
import PersonalProfileTab from '../components/PersonalProfileTab';

const SidebarItem = ({ icon: Icon, label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all duration-200 group relative overflow-hidden ${active
      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md shadow-blue-500/20'
      : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
      }`}
  >
    <div className="flex items-center gap-3 relative z-10 w-full">
      <div className={`p-1.5 rounded-lg transition-colors ${active ? 'bg-white/20' : 'bg-slate-800 group-hover:bg-slate-700'
        }`}>
        <Icon className={`h-4 w-4 ${active ? 'text-white' : 'text-slate-400 group-hover:text-blue-400'}`} />
      </div>
      <span className="font-medium text-sm truncate flex-1 text-left">{label}</span>
      {badge > 0 && (
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${active ? 'bg-white/20 text-white' : 'bg-blue-500/10 text-blue-400'
          }`}>
          {badge}
        </span>
      )}
    </div>
    {active && (
      <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
    )}
  </button>
);

const SourcingPartnerDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { showToast } = useToast();
  const { t } = useLanguage();
  const [candidates, setCandidates] = useState([]);
  const [pools, setPools] = useState([]);
  const [loading, setLoading] = useState(true);

  // Determine active tab from URL path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/pools')) return 'pools';
    if (path.includes('/notifications')) return 'notifications';
    if (path.includes('/my-profile')) return 'my-profile';
    if (path.includes('/team-members')) return 'team-members';
    if (path.includes('/profile')) return 'profile';
    if (path.includes('/feedback')) return 'feedback';
    return 'candidates';
  };
  const activeTab = getActiveTab();
  const [searchTerm, setSearchTerm] = useState('');
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ newPassword: '', confirmPassword: '' });
  const [passwordError, setPasswordError] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [publishingId, setPublishingId] = useState(null);
  const [fullUser, setFullUser] = useState(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);


  // Candidate modal states
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [candidateForm, setCandidateForm] = useState({
    name: '',
    email: '',
    phone: '',
    occupation: '',
    qualification: '',
    experience: '',
    german_level: '',
    university: '',
    year_of_passout: '',
    skills: '',
    password: '',
    progress_stage: 'Language Learning'
  });

  // Pool modal states
  const [showPoolModal, setShowPoolModal] = useState(false);
  const [poolForm, setPoolForm] = useState({ name: '', description: '', is_public: false });
  const [selectedPool, setSelectedPool] = useState(null);
  const [poolCandidates, setPoolCandidates] = useState([]);
  const [selectedCandidatesForPool, setSelectedCandidatesForPool] = useState([]);

  // Helper function to get the correct photo URL
  const getPhotoUrl = (photoUrl) => {
    if (!photoUrl) return null;
    let baseUrl = process.env.REACT_APP_API_URL;
    if (!baseUrl) {
      const origin = window.location.origin;
      baseUrl = origin.replace(':3000', ':8080');
    }
    if (baseUrl.endsWith('/api')) {
      baseUrl = baseUrl.slice(0, -4);
    } else if (baseUrl.includes('/api/')) {
      baseUrl = baseUrl.replace('/api/', '/');
    } else if (baseUrl.endsWith('/api/')) {
      baseUrl = baseUrl.slice(0, -5);
    }
    baseUrl = baseUrl.replace(/\/$/, '');
    const normalizedPhotoUrl = photoUrl.startsWith('/') ? photoUrl : `/${photoUrl}`;
    const timestamp = new Date().getTime();
    return `${baseUrl}${normalizedPhotoUrl}?t=${timestamp}`;
  };
  const [showAddToPoolModal, setShowAddToPoolModal] = useState(false);
  const [modalTab, setModalTab] = useState('add'); // 'add' or 'members'

  // My Feedbacks and Requests state
  const [myFeedbacks, setMyFeedbacks] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [feedbackResponses, setFeedbackResponses] = useState([]);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [notificationDateSort, setNotificationDateSort] = useState('desc');
  const [notificationStatusFilter, setNotificationStatusFilter] = useState('all');
  const [notificationFilter, setNotificationFilter] = useState('All');
  const [responseText, setResponseText] = useState('');
  const [isSendingResponse, setIsSendingResponse] = useState(false);

  // Feedback creation form state
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);

  const [feedbackForm, setFeedbackForm] = useState({
    message: '',
    feedback_type: 'feedback',
    priority: 'normal'
  });

  const fetchMyFeedbacks = async () => {
    try {
      // Fetch all feedbacks (both feedback and request types)
      const response = await api.get('/sourcing/my-feedbacks?feedback_type=all');
      setMyFeedbacks(response.data || []);
    } catch (error) {
      console.error('Error fetching my feedbacks:', error);
      setMyFeedbacks([]);
    }
  };

  const handleFeedbackSubmitted = () => {
    // Refresh feedbacks when new feedback is submitted
    fetchMyFeedbacks();
  };

  const fetchMyRequests = async () => {
    try {
      const response = await api.get('/sourcing/my-feedbacks?feedback_type=request');
      setMyRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching my requests:', error);
    }
  };

  const fetchFeedbackResponses = async (id) => {
    try {
      const response = await api.get(`/sourcing/my-feedbacks/${id}/responses`);
      setFeedbackResponses(response.data || []);
    } catch (error) {
      console.error('Error fetching feedback responses:', error);
    }
  };

  const handleFeedbackClick = (feedback) => {
    setSelectedFeedback(feedback);
    fetchFeedbackResponses(feedback.id);
    setShowFeedbackModal(true);
  };

  const handleSendResponse = async () => {
    if (!responseText.trim() || !selectedFeedback) return;
    setIsSendingResponse(true);
    try {
      await api.post(`/sourcing/my-feedbacks/${selectedFeedback.id}/respond`, {
        response_text: responseText
      });
      setResponseText('');
      fetchFeedbackResponses(selectedFeedback.id);
      fetchMyFeedbacks(); // To update response count if visible
    } catch (error) {
      console.error('Error sending response:', error);
      showToast('Failed to send response', 'error');
    } finally {
      setIsSendingResponse(false);
    }
  };

  const handleMarkFeedbackResolved = async () => {
    if (!selectedFeedback || !window.confirm('Are you sure you want to mark this as resolved?')) return;
    try {
      await api.put(`/sourcing/my-feedbacks/${selectedFeedback.id}/status`, {
        status: 'resolved'
      });
      setSelectedFeedback({ ...selectedFeedback, status: 'resolved' });
      fetchMyFeedbacks();
    } catch (error) {
      console.error('Error resolving feedback:', error);
      showToast('Failed to resolve feedback', 'error');
    }
  };


  const handleCreateFeedback = async () => {
    if (!feedbackForm.message.trim()) {
      showToast('Please enter a message', 'warning');
      return;
    }
    try {
      await api.post('/sourcing/my-feedbacks', {
        message: feedbackForm.message,
        feedback_type: feedbackForm.feedback_type,
        priority: feedbackForm.priority
      });
      // Reset form
      setFeedbackForm({
        message: '',
        feedback_type: 'feedback',
        priority: 'normal'
      });
      setShowFeedbackForm(false);
      // Refresh feedback list
      fetchMyFeedbacks();
      showToast('Feedback submitted successfully!', 'success');
    } catch (error) {
      console.error('Error creating feedback:', error);
      showToast('Failed to submit feedback. Please try again.', 'error');
    }
  };

  const fetchFullUser = async () => {
    try {
      console.log('Fetching full user profile...');
      const response = await api.get('/sourcing/profile');
      console.log('Full user response:', response.data);
      setFullUser(response.data);
    } catch (error) {
      console.error('Error fetching full user:', error);
      console.error('Error response:', error.response);

      let errorMsg = error.message;
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMsg = error.response.data;
        } else if (error.response.data.error) {
          errorMsg = error.response.data.error;
        } else {
          try {
            errorMsg = JSON.stringify(error.response.data);
          } catch (e) {
            errorMsg = 'Unknown error occurred';
          }
        }
      }

      // Set fullUser to an object with error flag to stop infinite loading
      setFullUser({ error: true, message: errorMsg });
    }
  };

  useEffect(() => {
    const initData = async () => {
      // Set loading to true only for initial complete load
      if (candidates.length === 0) setLoading(true);

      const path = location.pathname;
      const tasks = [];

      // Always fetch notifications and full user profile
      tasks.push(fetchNotifications());
      tasks.push(fetchFullUser());

      if (path.includes('/candidates')) {
        tasks.push(fetchCandidates());
      } else if (path.includes('/pools')) {
        tasks.push(fetchPools());
      } else if (path.includes('/feedback')) {
        tasks.push(fetchMyFeedbacks());
      } else if (path.includes('/settings')) {
        // any specific setting data
      }

      try {
        await Promise.all(tasks);
      } catch (err) {
        console.error('Initial data fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    initData();

    // Redirect to dashboard if on base sourcing path
    if (location.pathname === '/sourcing' || location.pathname === '/sourcing/') {
      navigate('/sourcing/candidates', { replace: true });
    }
  }, [location.pathname, navigate]);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/sourcing/notifications');
      setNotifications(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await api.put(`/sourcing/notifications/${notificationId}/read`);
      await fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };










  const fetchCandidates = async () => {
    try {
      const response = await api.get('/sourcing/candidates');
      setCandidates(response.data || []);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPools = async () => {
    try {
      const response = await api.get('/sourcing/pools');
      setPools(response.data || []);
    } catch (error) {
      console.error('Error fetching pools:', error);
    }
  };

  const fetchPoolCandidates = async (poolId) => {
    try {
      const response = await api.get(`/sourcing/pools/${poolId}/candidates`);
      setPoolCandidates(response.data || []);
    } catch (error) {
      console.error('Error fetching pool candidates:', error);
    }
  };

  const handleCreateCandidate = () => {
    setEditingCandidate(null);
    setCandidateForm({
      name: '', email: '', phone: '', occupation: '', qualification: '',
      experience: '', german_level: '', university: '', year_of_passout: '', skills: '', password: '',
      progress_stage: 'Language Learning'
    });
    setShowCandidateModal(true);
  };

  const handleEditCandidate = (candidate) => {
    setEditingCandidate(candidate);
    setCandidateForm({
      name: candidate.name || '',
      email: candidate.email || '',
      phone: candidate.phone || '',
      occupation: candidate.occupation || '',
      qualification: candidate.qualification || '',
      experience: candidate.experience || '',
      german_level: candidate.german_level || '',
      university: candidate.university || '',
      year_of_passout: candidate.year_of_passout || '',
      skills: candidate.skills || '',
      password: '',
      progress_stage: candidate.progress_stage || 'Language Learning'
    });
    setShowCandidateModal(true);
  };

  const handleSaveCandidate = async () => {
    try {
      if (editingCandidate) {
        await api.put(`/sourcing/candidates/${editingCandidate.id}`, candidateForm);
      } else {
        await api.post('/sourcing/candidates', candidateForm);
      }
      setShowCandidateModal(false);
      fetchCandidates();
    } catch (error) {
      console.error('Error saving candidate:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to save candidate';
      showToast(`Error: ${errorMessage}`, 'error');
    }
  };

  const handleCreatePool = async () => {
    try {
      await api.post('/sourcing/pools', poolForm);
      setShowPoolModal(false);
      setPoolForm({ name: '', description: '', is_public: false });
      fetchPools();
      showToast('Pool created successfully', 'success');
    } catch (error) {
      console.error('Error creating pool:', error);
      showToast(error.response?.data?.error || 'Failed to create pool', 'error');
    }
  };

  const handleTogglePoolVisibility = async (pool, currentVisibility) => {
    try {
      await api.put(`/sourcing/pools/${pool.id}`, {
        name: pool.name,
        description: pool.description || '',
        is_public: !currentVisibility
      });
      showToast('Pool visibility updated', 'success');
      fetchPools();
    } catch (error) {
      showToast('Failed to update pool visibility', 'error');
    }
  };

  const handleManagePool = async (pool) => {
    setSelectedPool(pool);
    setSelectedCandidatesForPool([]); // Clear any previous selections
    setModalTab('members'); // Default to member list
    await fetchPoolCandidates(pool.id);
    setShowAddToPoolModal(true);
  };

  const handleAddCandidatesToPool = async () => {
    if (!selectedPool || selectedCandidatesForPool.length === 0) return;
    try {
      const response = await api.post(`/sourcing/pools/${selectedPool.id}/candidates`, {
        candidate_ids: selectedCandidatesForPool
      });
      const successCount = response.data.success_count;
      if (successCount === 0) {
        showToast('No candidates were added. They might already be in the pool or you might not have permission.', 'info');
      } else {
        showToast(`Successfully added ${successCount} candidate(s) to the pool.`, 'success');
      }

      setShowAddToPoolModal(false);
      setSelectedCandidatesForPool([]);

      // Refresh data
      await fetchPoolCandidates(selectedPool.id);
      await fetchPools();
      await fetchCandidates();
    } catch (error) {
      console.error('Error adding candidates to pool:', error);
      showToast('Failed to add candidates to pool', 'error');
    }
  };

  const handleRemoveCandidateFromPool = async (candidateId) => {
    if (!selectedPool || !window.confirm('Are you sure you want to remove this candidate from the pool?')) return;
    try {
      await api.delete(`/sourcing/pools/${selectedPool.id}/candidates/${candidateId}`);
      // Refresh data
      await fetchPoolCandidates(selectedPool.id);
      await fetchPools();
      showToast('Candidate removed from pool', 'success');
    } catch (error) {
      console.error('Error removing candidate from pool:', error);
      showToast('Failed to remove candidate from pool', 'error');
    }
  };

  const handlePublishCandidates = async (candidateIds) => {
    if (!window.confirm(`Are you sure you want to publish ${candidateIds.length} candidate(s)?`)) {
      return;
    }
    try {
      await api.post('/sourcing/candidates/publish', { candidate_ids: candidateIds });
      // alert('Candidates published successfully! Staff will be notified for approval.');
      fetchCandidates();
      showToast('Candidates published successfully!', 'success');
    } catch (error) {
      console.error('Error publishing candidates:', error);
      showToast('Failed to publish candidates', 'error');
    }
  };

  const handlePasswordReset = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    try {
      await api.put('/auth/reset-password', { password: passwordForm.newPassword });
      showToast('Password reset successfully', 'success');
      setShowPasswordModal(false);
      setPasswordForm({ newPassword: '', confirmPassword: '' });
      setPasswordError('');
    } catch (error) {
      setPasswordError(error.response?.data?.error || 'Failed to reset password');
    }
  };

  const handleDeleteCandidate = async (id) => {
    if (!window.confirm('Are you sure you want to delete this candidate?')) return;
    try {
      await api.delete(`/sourcing/candidates/${id}`);
      fetchCandidates();
      showToast('Candidate deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting candidate:', error);
      showToast('Failed to delete candidate', 'error');
    }
  };

  const handleDeletePool = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pool?')) return;
    try {
      await api.delete(`/sourcing/pools/${id}`);
      fetchPools();
      showToast('Pool deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting pool:', error);
      showToast('Failed to delete pool', 'error');
    }
  };

  const handlePublishCandidate = async (id) => {
    setPublishingId(id);
    await handlePublishCandidates([id]);
    setPublishingId(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const filteredCandidates = candidates.filter(c => {
    const matchesSearch = !searchTerm ||
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.occupation?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' ||
      c.profile_status === filterStatus ||
      c.progress_stage === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-800',
      published: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex font-sans text-slate-900 text-sm">
      <div className="w-60 bg-slate-900 border-r border-slate-800 shadow-2xl z-30 flex flex-col transition-all duration-300">
        <div className="p-4 border-b border-slate-800/50 bg-slate-900/50 backdrop-blur-sm flex items-center gap-2.5">
          <div className="relative">
            <div className="h-10 w-10 bg-white rounded-lg flex items-center justify-center p-1 shadow-lg shadow-blue-500/10 border border-slate-700">
              {fullUser?.logo_url ? (
                <img src={getPhotoUrl(fullUser.logo_url)} alt="Logo" className="h-full w-full object-contain" />
              ) : (
                <img src="/dashboard_logo.png" alt="Polyglots" className="h-full w-full object-contain" />
              )}
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 bg-emerald-500 rounded-full border border-slate-900"></div>
          </div>
          <div>
            <h1 className="text-sm font-bold text-white tracking-tight">Polyglots</h1>
            <p className="text-[11px] text-slate-400 font-medium whitespace-nowrap">{t('roles.sourcingPartner') || 'Sourcing Partner Portal'}</p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1 custom-scrollbar">
          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 px-2">{t('dashboard.main') || 'Main Menu'}</p>
          <SidebarItem
            icon={Users}
            label={t('adminDashboard.candidates') || 'Candidates'}
            active={activeTab === 'candidates'}
            onClick={() => navigate('/sourcing/candidates')}
            badge={candidates.filter(c => c.status === 'pending').length}
          />

          <SidebarItem
            icon={Folder}
            label={t('dashboard.talentPools') || 'Talent Pools'}
            active={activeTab === 'pools'}
            onClick={() => navigate('/sourcing/pools')}
          />

          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 mt-4 px-2">{t('dashboard.communication') || 'Communication'}</p>
          <SidebarItem
            icon={MessageSquare}
            label={t('dashboard.feedbackSupport') || 'Feedback & Support'}
            active={activeTab === 'feedback'}
            onClick={() => navigate('/sourcing/feedback')}
          />
          <SidebarItem
            icon={Bell}
            label={t('dashboard.notifications') || 'Notifications'}
            active={activeTab === 'notifications'}
            onClick={() => navigate('/sourcing/notifications')}
            badge={notifications.filter(n => !n.is_read).length}
          />

          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-2 mt-4 px-2">{t('dashboard.account') || 'Account'}</p>
          <SidebarItem
            icon={Building2}
            label={t('dashboard.companyProfile') || 'Company Profile'}
            active={activeTab === 'profile'}
            onClick={() => navigate('/sourcing/profile')}
          />
          <SidebarItem
            icon={User}
            label={t('dashboard.myProfile') || 'My Profile'}
            active={activeTab === 'my-profile'}
            onClick={() => navigate('/sourcing/my-profile')}
          />
          <SidebarItem
            icon={Users}
            label={t('dashboard.teamMembers') || 'Team Members'}
            active={activeTab === 'team-members'}
            onClick={() => navigate('/sourcing/team-members')}
          />
          <SidebarItem
            icon={Settings}
            label={t('dashboard.settings') || 'Settings'}
            active={activeTab === 'settings'}
            onClick={() => navigate('/sourcing/settings')}
          />
        </nav>

        <div className="p-3 border-t border-slate-800 bg-slate-900/50">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all group text-xs"
          >
            <LogOut className="h-4 w-4 group-hover:rotate-180 transition-transform duration-300" />
            <span className="font-medium">{t('navigation.signOut') || 'Sign Out'}</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden bg-slate-50 relative">
        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-blue-50/50 to-transparent pointer-events-none" />
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-50/30 rounded-full blur-3xl pointer-events-none translate-x-1/2 -translate-y-1/2" />

        {/* Header - Sticky Glassmorphism */}
        <header className="bg-white/80 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-40 shadow-sm">
          <div className="w-full px-5 py-3">
            <div className="flex items-center justify-between gap-4">
              {/* Search */}
              <div className="flex items-center gap-2 flex-1">
                <div className="relative max-w-sm group hidden md:block flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg leading-5 bg-slate-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm text-sm"
                    placeholder="Search candidates, pools..."
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="flex flex-col items-end hidden sm:flex">
                    <span className="text-sm font-semibold text-slate-700">{fullUser?.name || user?.name}</span>
                    <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">{fullUser?.legal_entity_name || t('roles.sourcingPartner') || 'Sourcing Partner'}</span>
                  </div>

                  {/* Language Toggle */}
                  <LanguageSelector />

                  <button
                    onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                    className="relative h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm hover:shadow-lg hover:shadow-blue-500/25 transition-all group overflow-hidden"
                  >
                    {fullUser?.photo_url || user?.photo_url ? (
                      <img src={getPhotoUrl(fullUser?.photo_url || user?.photo_url)} alt={user?.name} className="h-full w-full object-cover" />
                    ) : (
                      (fullUser?.name || user?.name)?.charAt(0).toUpperCase()
                    )}
                    <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 bg-emerald-500 border-2 border-white rounded-full z-10" />

                    {showProfileDropdown && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setShowProfileDropdown(false)} />
                        <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-xl py-2 z-20 animate-in fade-in zoom-in duration-200 origin-top-right">
                          <div className="px-4 py-3 border-b border-gray-100 mb-1">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Account Info</p>
                            <p className="text-xs font-bold text-slate-800 truncate">{user?.email}</p>
                          </div>
                          <button
                            onClick={() => { navigate('/sourcing/my-profile'); setShowProfileDropdown(false); }}
                            className="w-full text-left px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-600 flex items-center gap-2.5 transition-colors"
                          >
                            <User className="h-4 w-4" /> <span className="font-medium">My Profile</span>
                          </button>
                          <button
                            onClick={() => { setShowPasswordModal(true); setShowProfileDropdown(false); }}
                            className="w-full text-left px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-600 flex items-center gap-2.5 transition-colors"
                          >
                            <Key className="h-4 w-4" /> <span className="font-medium">Reset Password</span>
                          </button>
                          <div className="h-px bg-gray-100 my-1" />
                          <button
                            onClick={handleLogout}
                            className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50 flex items-center gap-2.5 transition-colors"
                          >
                            <LogOut className="h-4 w-4" /> <span className="font-medium">Sign Out</span>
                          </button>
                        </div>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 scroll-smooth">
          {activeTab === 'candidates' && (
            <>
              {!editingCandidate ? (
                <div className="max-w-7xl mx-auto space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-700">
                  <div className="flex justify-between items-end">
                    <div>
                      <h2 className="text-base font-bold text-slate-800 tracking-tight">Candidates</h2>
                      <p className="text-slate-500 uppercase text-[8px] font-bold tracking-widest opacity-70">Manage Your Sourced Candidates</p>
                    </div>
                    <button
                      onClick={() => {
                        setEditingCandidate(null);
                        setShowCandidateModal(true);
                      }}
                      className="px-3 py-1.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 shadow-sm transition-all flex items-center gap-1.5 group"
                    >
                      <Plus className="h-3 w-3 group-hover:rotate-90 transition-transform duration-300" />
                      <span className="uppercase tracking-widest text-[9px]">Add Candidate</span>
                    </button>
                  </div>

                  {/* Compact Filters */}
                  <div className="flex bg-white p-1 rounded-lg border border-slate-200 shadow-sm">
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="w-full px-3 py-2 bg-transparent text-[11px] font-bold uppercase tracking-widest text-slate-600 focus:outline-none cursor-pointer"
                    >
                      <option value="all">All Status</option>
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="approved">Approved</option>
                      <option value="Language Learning">Language Learning</option>
                      <option value="Document Submitted">Document Submitted</option>
                      <option value="Screening Waiting">Screening Waiting</option>
                      <option value="Screening Passed">Screening Passed</option>
                      <option value="Interview Waiting">Interview Waiting</option>
                      <option value="Interview Scheduled">Interview Scheduled</option>
                      <option value="Interview attended">Interview attended</option>
                      <option value="Offer Received">Offer Received</option>
                      <option value="Visa submitted">Visa submitted</option>
                      <option value="Visa Granted">Visa Granted</option>
                      <option value="Migrated">Migrated</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>

                  {loading ? (
                    <div className="flex flex-col items-center justify-center py-12 bg-white rounded-xl border border-slate-200 border-dashed">
                      <div className="relative h-8 w-8 mb-2">
                        <div className="absolute inset-0 border-2 border-blue-500/10 rounded-full" />
                        <div className="absolute inset-0 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                      </div>
                      <p className="text-slate-400 font-bold uppercase tracking-widest text-[8px] animate-pulse">Loading candidates...</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {candidates
                        .filter(c => filterStatus === 'all' || c.profile_status === filterStatus)
                        .filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          c.email.toLowerCase().includes(searchTerm.toLowerCase()))
                        .map((candidate) => (
                          <div key={candidate.id} className="group bg-white rounded-2xl p-6 border border-gray-100 hover:border-blue-100 hover:shadow-2xl hover:shadow-blue-500/5 transition-all duration-500 relative overflow-hidden">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex gap-4">
                                <div className="relative">
                                  {candidate.photo_url ? (
                                    <img
                                      src={candidate.photo_url.startsWith('http') ? candidate.photo_url : `${process.env.REACT_APP_API_URL}${candidate.photo_url}`}
                                      alt={candidate.name}
                                      className="h-14 w-14 rounded-full object-cover border-2 border-slate-50 shadow-sm"
                                    />
                                  ) : (
                                    <div className="h-14 w-14 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 border-2 border-white shadow-sm flex items-center justify-center text-slate-400 font-bold text-xl uppercase">
                                      {candidate.name.charAt(0)}
                                    </div>
                                  )}
                                  <div className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 bg-emerald-500 border-2 border-white rounded-full shadow-sm" />
                                </div>
                                <div className="flex flex-col min-w-0">
                                  <h3 className="text-base font-bold text-slate-800 uppercase tracking-tight truncate leading-tight group-hover:text-blue-600 transition-colors">
                                    {candidate.name}
                                  </h3>
                                  <span className="text-sm text-slate-400 font-medium truncate mb-2">{candidate.email}</span>
                                </div>
                              </div>
                              <button
                                onClick={() => setEditingCandidate(candidate)}
                                className="p-2 text-slate-300 hover:text-blue-500 hover:bg-blue-50 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </div>

                            <div className="space-y-2.5 mb-6">
                              <div className="flex items-center gap-3 text-slate-500">
                                <Phone className="h-4 w-4 text-slate-300" />
                                <span className="text-sm font-semibold">{candidate.phone || 'N/A'}</span>
                              </div>
                              <div className="flex items-center gap-3 text-slate-500">
                                <MapPin className="h-4 w-4 text-slate-300" />
                                <span className="text-sm font-semibold">{candidate.address || candidate.location || 'N/A'}</span>
                              </div>
                            </div>

                            <div className="flex items-center gap-3 mb-4">
                              <div className="p-1 px-2 border border-red-100 bg-red-50/30 rounded flex items-center gap-1.5">
                                <Users className="h-3 w-3 text-red-500" />
                                <span className="text-[10px] font-black text-red-500 uppercase tracking-widest">Pool</span>
                              </div>
                              <div className="px-5 py-2.5 bg-slate-50 rounded-xl border border-slate-100 flex-1">
                                <span className="text-xs font-bold text-slate-500 truncate block">
                                  {candidate.pool_name || candidate.occupation || 'General Pool'}
                                </span>
                              </div>
                            </div>

                            {candidate.progress_stage && (
                              <div className="flex items-center gap-3 mb-4">
                                <div className="p-1 px-2 border border-emerald-100 bg-emerald-50/30 rounded flex items-center gap-1.5">
                                  <CheckCircle className="h-3 w-3 text-emerald-500" />
                                  <span className="text-[10px] font-black text-emerald-500 uppercase tracking-widest">App Status</span>
                                </div>
                                <div className="px-5 py-2.5 bg-slate-50 rounded-xl border border-slate-100 flex-1">
                                  <span className="text-xs font-bold text-slate-500 truncate block">
                                    {candidate.progress_stage || 'Language Learning'}
                                  </span>
                                </div>
                              </div>
                            )}

                            <div className="flex items-center gap-3 mb-8">
                              <div className="p-1 px-2 border border-blue-100 bg-blue-50/30 rounded flex items-center gap-1.5">
                                <Activity className="h-3 w-3 text-blue-500" />
                                <span className="text-[10px] font-black text-blue-500 uppercase tracking-widest">Profile Status</span>
                              </div>
                              <div className="px-5 py-2.5 bg-slate-50 rounded-xl border border-slate-100 flex-1">
                                <span className="text-xs font-bold text-slate-500 truncate block">
                                  {candidate.profile_status || 'Draft'}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center justify-between border-t border-slate-50 pt-5">
                              <div className="flex gap-3">
                                <button
                                  onClick={() => setEditingCandidate(candidate)}
                                  className="h-10 w-10 flex items-center justify-center bg-blue-50 border border-blue-100 text-blue-600 rounded-xl hover:bg-blue-600 hover:text-white transition-all shadow-sm group/btn"
                                  title="View Profile"
                                >
                                  <FileText className="h-5 w-5" />
                                </button>
                              </div>
                              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                                {new Date(candidate.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </span>
                            </div>

                            {candidate.profile_status === 'draft' && (
                              <button
                                onClick={() => handlePublishCandidate(candidate.id)}
                                disabled={publishingId === candidate.id}
                                className="absolute top-4 right-12 px-3 py-1 bg-amber-50 text-amber-600 text-[9px] font-black uppercase tracking-widest rounded-lg border border-amber-200 hover:bg-amber-600 hover:text-white transition-all"
                              >
                                {publishingId === candidate.id ? 'Publishing...' : 'Publish'}
                              </button>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="max-w-7xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setEditingCandidate(null)}
                      className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl transition-all font-bold text-sm"
                    >
                      <ChevronLeft className="h-5 w-5" />
                      Back to Candidates
                    </button>
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Edit Candidate Profile</h2>
                      <p className="text-slate-500 mt-0.5 uppercase text-[9px] font-bold tracking-widest opacity-70">Edit candidate details</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
                    <CandidateProfileView
                      candidate={editingCandidate}
                      embedded={true}
                      onClose={() => setEditingCandidate(null)}
                      onSaved={async () => {
                        await fetchCandidates();
                        await fetchPools();
                        // Stay in view to allow further edits
                      }}
                    />
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === 'pools' && (
            <div className="max-w-7xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
              <div className="flex justify-between items-end">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Talent Pools</h2>
                  <p className="text-slate-500 mt-0.5 uppercase text-[9px] font-bold tracking-widest opacity-70">Organize Candidates into Groups</p>
                </div>
                <button
                  onClick={() => setShowPoolModal(true)}
                  className="px-6 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-md hover:shadow-lg transition-all flex items-center gap-2 group"
                >
                  <Plus className="h-4 w-4 group-hover:rotate-90 transition-transform duration-300" />
                  <span className="uppercase tracking-widest text-[10px]">Create New Pool</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pools.filter(pool =>
                  !searchTerm ||
                  pool.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  pool.description?.toLowerCase().includes(searchTerm.toLowerCase())
                ).length === 0 ? (
                  <div className="col-span-full py-24 text-center bg-white rounded-3xl border border-slate-200 border-dashed">
                    <div className="h-12 w-12 bg-slate-50 rounded-xl flex items-center justify-center mx-auto mb-4 border border-slate-100">
                      <Folder className="h-6 w-6 text-slate-300" />
                    </div>
                    <p className="text-slate-400 font-bold uppercase tracking-widest text-[9px]">{searchTerm ? `No pools found matching "${searchTerm}"` : 'No talent pools created yet'}</p>
                  </div>
                ) : (
                  pools
                    .filter(pool =>
                      !searchTerm ||
                      pool.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      pool.description?.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((pool) => (
                      <div key={pool.id} className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-blue-400 hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-300 shadow-sm relative overflow-hidden">
                        <div className="flex justify-between items-start mb-6">
                          <div className="h-10 w-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 shadow-inner group-hover:scale-110 transition-transform">
                            <Folder className="h-5 w-5" />
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleTogglePoolVisibility(pool, pool.is_public)}
                              className={`p-1.5 rounded-lg transition-all ${pool.is_public ? 'text-emerald-600 hover:bg-emerald-50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'}`}
                              title={pool.is_public ? "Public (click to make private)" : "Private (click to make public)"}
                            >
                              {pool.is_public ? <Globe className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                            </button>
                            <span className="px-3 py-1 bg-slate-50 rounded-lg text-[9px] font-bold text-slate-500 border border-slate-100 uppercase tracking-widest">
                              {pool.candidates?.length || 0} Candidates
                            </span>
                          </div>
                        </div>

                        <h3 className="text-base font-bold text-slate-800 mb-1.5 uppercase tracking-tight group-hover:text-blue-600 transition-colors">{pool.name}</h3>
                        <p className="text-slate-500 text-[11px] line-clamp-2 mb-6 min-h-[32px] leading-relaxed font-medium">{pool.description || 'No description provided.'}</p>

                        <button
                          onClick={() => handleManagePool(pool)}
                          className="w-full py-2.5 bg-slate-50 hover:bg-blue-600 text-slate-600 hover:text-white font-bold rounded-xl border border-slate-200 hover:border-blue-600 transition-all text-[9px] uppercase tracking-widest flex items-center justify-center gap-2 group/btn"
                        >
                          <Settings className="h-3.5 w-3.5 group-hover/btn:rotate-180 transition-transform duration-700" />
                          Manage Pool
                        </button>
                      </div>
                    ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-6 duration-700">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-3xl font-black text-slate-900 tracking-tight">{t('dashboard.notifications') || 'Notifications'}</h2>
                </div>
                <button
                  onClick={() => notifications.forEach(n => !n.is_read && handleMarkAsRead(n.id))}
                  className="text-xs font-bold text-blue-600 hover:text-blue-700 underline underline-offset-4"
                >
                  {t('dashboard.markAllRead') || 'Mark all as read'}
                </button>
              </div>

              {/* Notification Filter Tabs */}
              <div className="flex items-center gap-1.5 p-1 bg-gray-100 rounded-2xl w-fit">
                {['All', 'Program', 'Pool', 'System'].map((tab) => {
                  const counts = {
                    All: notifications.filter(n => !n.is_read).length,
                    Program: notifications.filter(n => !n.is_read && (n.type?.includes('program') || n.type?.includes('feedback'))).length,
                    Pool: notifications.filter(n => !n.is_read && n.type?.includes('pool')).length,
                    System: notifications.filter(n => !n.is_read && n.type?.includes('system')).length,
                  };
                  return (
                    <button
                      key={tab}
                      onClick={() => setNotificationFilter(tab)}
                      className={`relative px-6 py-2.5 text-sm font-bold rounded-xl transition-all duration-300 ${notificationFilter === tab
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25'
                        : 'text-slate-500 hover:bg-white hover:shadow-sm'
                        }`}
                    >
                      {tab}
                      {counts[tab] > 0 && (
                        <div className="absolute top-2 right-2 h-1.5 w-1.5 bg-red-500 rounded-full border border-white" />
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="bg-white rounded-[32px] border border-slate-200 overflow-hidden shadow-2xl shadow-slate-200/50">
                {notifications.length > 0 ? (
                  notifications
                    .filter(n => {
                      if (notificationFilter === 'All') return true;
                      if (notificationFilter === 'Program') return n.type?.includes('program') || n.type?.includes('feedback');
                      if (notificationFilter === 'Pool') return n.type?.includes('pool');
                      if (notificationFilter === 'System') return n.type?.includes('system');
                      return true;
                    })
                    .map((notif) => (
                      <div key={notif.id} className={`p-6 border-b border-slate-50 flex items-start gap-5 hover:bg-slate-50/50 transition-all group relative ${!notif.is_read ? 'bg-blue-50/10' : ''}`}>
                        <div className={`mt-1 h-14 w-14 rounded-full border flex items-center justify-center shrink-0 transition-transform group-hover:scale-110 duration-500 ${!notif.is_read ? 'bg-slate-900 border-slate-800 text-white' : 'bg-slate-50 border-slate-100 text-slate-400'}`}>
                          <Star className="h-7 w-7" />
                        </div>
                        <div className="flex-1 min-w-0 pr-8">
                          <div className="flex items-center gap-2 mb-1.5">
                            <p className={`text-base leading-snug ${!notif.is_read ? 'text-slate-900 font-bold' : 'text-slate-600'}`}>{notif.message}</p>
                            {!notif.is_read && <div className="h-2 w-2 rounded-full bg-red-500 shrink-0 shadow-sm" />}
                          </div>
                          <p className="text-sm text-slate-400 font-bold uppercase tracking-widest opacity-60">
                            {new Date(notif.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} | {new Date(notif.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button className="p-2 text-slate-300 hover:text-slate-600 transition-colors">
                            <MoreHorizontal className="h-5 w-5" />
                          </button>
                        </div>

                        {!notif.is_read && (
                          <button
                            onClick={() => handleMarkAsRead(notif.id)}
                            className="absolute inset-x-0 bottom-0 top-0 w-full z-0 cursor-default"
                          />
                        )}
                      </div>
                    ))
                ) : (
                  <div className="py-32 text-center">
                    <div className="h-20 w-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 border border-slate-100">
                      <Star className="h-10 w-10 text-slate-200" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">{t('dashboard.noNotifications') || 'No notifications yet'}</h3>
                    <p className="text-slate-400 font-medium">We'll alert you when something important happens.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'feedback' && (
            <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700">
              <div className="flex justify-between items-end border-b border-slate-200 pb-6">
                <div>
                  <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Feedback & Support</h2>
                  <p className="text-slate-500 mt-0.5 uppercase text-[9px] font-bold tracking-widest opacity-70">Submit feedback or support requests</p>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowFeedbackForm(!showFeedbackForm)}
                    className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm rounded-lg transition-all shadow-sm"
                  >
                    <Plus className="h-4 w-4" /> New Feedback
                  </button>
                  <div className="px-4 py-2 bg-white rounded-xl border border-slate-200 text-[9px] font-bold text-blue-600 uppercase tracking-widest flex items-center gap-2 shadow-sm">
                    <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" /> Online
                  </div>
                </div>
              </div>

              {/* Feedback Creation Form */}
              {showFeedbackForm && (
                <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                  <h3 className="text-lg font-bold text-slate-800 mb-4">Create New Feedback</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Message *</label>
                      <textarea
                        value={feedbackForm.message}
                        onChange={(e) => setFeedbackForm({ ...feedbackForm, message: e.target.value })}
                        placeholder="Describe your feedback or support request..."
                        className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-slate-900 resize-none"
                        rows="4"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Type</label>
                        <select
                          value={feedbackForm.feedback_type}
                          onChange={(e) => setFeedbackForm({ ...feedbackForm, feedback_type: e.target.value })}
                          className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-slate-900 cursor-pointer"
                        >
                          <option value="feedback">Feedback</option>
                          <option value="request">Request</option>
                          <option value="issue">Issue</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Priority</label>
                        <select
                          value={feedbackForm.priority}
                          onChange={(e) => setFeedbackForm({ ...feedbackForm, priority: e.target.value })}
                          className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 text-slate-900 cursor-pointer"
                        >
                          <option value="normal">Normal</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                      <button
                        onClick={() => {
                          setShowFeedbackForm(false);
                          setFeedbackForm({ message: '', feedback_type: 'feedback', priority: 'normal' });
                        }}
                        className="px-5 py-2.5 border border-slate-200 text-slate-700 font-semibold text-sm rounded-lg hover:bg-slate-50 transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateFeedback}
                        className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm rounded-lg transition-all shadow-sm"
                      >
                        Submit Feedback
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid gap-4">
                {myFeedbacks.map((fb) => (
                  <div key={fb.id} className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-blue-400 transition-all duration-300 shadow-sm">
                    <div className="flex justify-between items-start gap-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-4">
                          <span className={`px-2 py-0.5 rounded-lg text-[8px] font-bold uppercase tracking-widest ${fb.feedback_type === 'request' ? 'bg-indigo-50 text-indigo-600' : 'bg-blue-50 text-blue-600'
                            }`}>
                            {fb.feedback_type}
                          </span>
                          <span className="text-slate-300">•</span>
                          <span className={`text-[8px] font-bold uppercase tracking-widest ${fb.priority === 'urgent' ? 'text-red-600' : 'text-slate-400'
                            }`}>
                            {fb.priority} Priority
                          </span>
                          <span className="text-slate-800 font-bold">•</span>
                          <span className="text-[9px] font-black text-blue-500 uppercase tracking-widest italic">{fb.status}</span>
                        </div>
                        <p className="text-slate-800 text-lg font-bold italic leading-tight mb-6 group-hover:text-blue-600 transition-colors uppercase tracking-tight">{fb.message}</p>
                        <div className="flex items-center gap-6 text-[10px] text-slate-500 font-black uppercase tracking-widest italic opacity-60">
                          <span className="flex items-center gap-2"><MessageSquare className="h-3.5 w-3.5" /> {fb.response_count || 0} Responses</span>
                          <span>Updated: {new Date(fb.updated_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleFeedbackClick(fb)}
                        className="px-8 py-3.5 bg-slate-800 hover:bg-blue-600 text-slate-400 hover:text-white font-black rounded-2xl border border-white/5 hover:border-blue-500/30 transition-all duration-300 text-[10px] uppercase tracking-[0.2em] shadow-2xl"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}

                {myFeedbacks.length === 0 && (
                  <div className="py-40 text-center bg-white rounded-3xl border border-slate-200 border-dashed">
                    <MessageSquare className="h-16 w-16 text-slate-300 mx-auto mb-6 opacity-50" />
                    <p className="text-slate-400 font-bold uppercase tracking-widest text-[10px] italic">No feedback submitted yet</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'profile' && (
            <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700">
              <div className="border-b border-slate-200 pb-6">
                <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Company Profile</h2>
                <p className="text-slate-500 mt-0.5 uppercase text-[9px] font-bold tracking-widest opacity-70">Your Company & Contact Details</p>
              </div>

              <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden min-h-[600px] relative">
                {fullUser ? (
                  fullUser.error ? (
                    <div className="flex flex-col items-center justify-center py-40">
                      <XCircle className="h-16 w-16 text-red-400 mb-4" />
                      <p className="text-slate-600 font-bold text-sm mb-2">Failed to Load Profile</p>
                      <p className="text-slate-400 text-xs max-w-md text-center">
                        {fullUser.message || 'Unable to fetch profile data. Please try again.'}
                      </p>
                      <button
                        onClick={() => fetchFullUser()}
                        className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-sm font-bold"
                      >
                        Retry
                      </button>
                    </div>
                  ) : isEditingProfile ? (
                    <div className="p-6">
                      <div className="mb-6 flex justify-between items-center bg-slate-50 p-4 rounded-2xl border border-slate-100">
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-100 p-2 rounded-xl">
                            <Edit className="h-5 w-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-slate-800">Editing Profile</h3>
                            <p className="text-xs text-slate-500">Update your company and contact information</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setIsEditingProfile(false)}
                          className="px-4 py-2 text-slate-600 hover:text-slate-900 font-bold text-sm bg-white border border-slate-200 rounded-xl transition-all shadow-sm"
                        >
                          Cancel
                        </button>
                      </div>
                      <ProfileEditor
                        user={fullUser}
                        mode="edit"
                        onClose={() => setIsEditingProfile(false)}
                        onSaved={() => {
                          fetchFullUser();
                          setIsEditingProfile(false);
                        }}
                        role="sourcing_partner"
                      />
                    </div>
                  ) : (
                    <div className="p-8">
                      {/* View Only Mode */}
                      <div className="mb-10 flex justify-between items-start">
                        <div className="flex items-center gap-6">
                          <div className="h-24 w-24 rounded-3xl bg-slate-100 flex items-center justify-center border-2 border-slate-200 shadow-sm overflow-hidden">
                            {fullUser.photo_url ? (
                              <img src={getPhotoUrl(fullUser.photo_url)} alt={fullUser.legal_entity_name || fullUser.name} className="h-full w-full object-cover" />
                            ) : (
                              <Building2 className="h-10 w-10 text-slate-300" />
                            )}
                          </div>
                          <div>
                            <h3 className="text-3xl font-black text-slate-900 tracking-tight">{fullUser.legal_entity_name || fullUser.name}</h3>
                            <p className="text-slate-500 font-medium flex items-center gap-2 mt-1">
                              <Globe className="h-4 w-4 text-blue-500" />
                              {fullUser.company_website || 'No website provided'}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setIsEditingProfile(true)}
                          className="px-6 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-md hover:shadow-lg transition-all flex items-center gap-2 uppercase tracking-widest text-[10px]"
                        >
                          <Edit className="h-4 w-4" />
                          Edit Profile
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-8">
                          <div>
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-blue-500" />
                              Company Information
                            </h4>
                            <div className="bg-slate-50/50 rounded-2xl p-6 border border-slate-100 space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Trade Name</p>
                                  <p className="text-sm font-bold text-slate-700">{fullUser.trade_name || 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Founded</p>
                                  <p className="text-sm font-bold text-slate-700">{fullUser.year_of_establishment || 'N/A'}</p>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Company Size</p>
                                  <p className="text-sm font-bold text-slate-700">{fullUser.company_size || 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Specialization</p>
                                  <p className="text-sm font-bold text-slate-700">{fullUser.specialization || 'N/A'}</p>
                                </div>
                              </div>
                              <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Corporate Address</p>
                                <p className="text-sm font-bold text-slate-700 leading-relaxed">{fullUser.address || 'No address provided'}</p>
                              </div>
                            </div>
                          </div>

                          <div>
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                              Point of Contact
                            </h4>
                            <div className="bg-emerald-50/20 rounded-2xl p-6 border border-emerald-100/50 space-y-4">
                              <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Account Manager</p>
                                <p className="text-sm font-bold text-slate-700">{fullUser.account_manager_name || fullUser.name}</p>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Email</p>
                                  <p className="text-sm font-bold text-slate-700 truncate">{fullUser.official_email || fullUser.email}</p>
                                </div>
                                <div>
                                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Phone</p>
                                  <p className="text-sm font-bold text-slate-700">{fullUser.direct_phone || fullUser.phone || 'N/A'}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-8">
                          <div>
                            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                              <div className="h-1.5 w-1.5 rounded-full bg-purple-500" />
                              Compliance Documents
                            </h4>
                            <div className="grid grid-cols-1 gap-3">
                              {[
                                { name: 'Incorporation Cert', url: fullUser.incorporation_certificate_url },
                                { name: 'Labor License', url: fullUser.labor_license_url },
                                { name: 'MSME Certificate', url: fullUser.msme_diversity_certificate_url }
                              ].map((doc, idx) => (
                                <div key={idx} className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-2xl shadow-sm hover:border-purple-200 transition-all">
                                  <div className="flex items-center gap-3">
                                    <div className="p-2 bg-purple-50 rounded-lg text-purple-600">
                                      <FileText className="h-4 w-4" />
                                    </div>
                                    <span className="text-xs font-bold text-slate-700">{doc.name}</span>
                                  </div>
                                  {doc.url ? (
                                    <a href={doc.url} target="_blank" rel="noopener noreferrer" className="text-[10px] font-bold text-blue-600 hover:underline uppercase tracking-wider">View File</a>
                                  ) : (
                                    <span className="text-[10px] font-bold text-slate-300 uppercase tracking-wider">Not Uploaded</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="bg-blue-600 rounded-3xl p-8 text-white relative overflow-hidden shadow-xl shadow-blue-500/20">
                            <div className="relative z-10">
                              <h4 className="text-lg font-black mb-2">Need to update your details?</h4>
                              <p className="text-blue-100 text-xs font-medium leading-relaxed mb-6">Changing your legal name or tax details may require manual verification by our staff team.</p>
                              <button
                                onClick={() => setIsEditingProfile(true)}
                                className="px-6 py-2.5 bg-white text-blue-600 font-bold rounded-xl hover:bg-blue-50 transition-all text-[10px] uppercase tracking-widest shadow-lg"
                              >
                                Start Editing
                              </button>
                            </div>
                            <Building2 className="absolute -bottom-4 -right-4 h-32 w-32 text-blue-500/20 rotate-12" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="flex flex-col items-center justify-center py-40">
                    <div className="h-12 w-12 border-4 border-blue-500/10 border-t-blue-600 rounded-full animate-spin mb-4" />
                    <p className="text-slate-400 font-bold uppercase tracking-widest text-[9px]">Loading profile...</p>
                  </div>
                )}

              </div>
            </div>
          )}

          {/* My Profile Tab */}
          {activeTab === 'my-profile' && (
            <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700">
              <PersonalProfileTab user={fullUser} role="sourcing_partner" onSaved={fetchFullUser} />
            </div>
          )}

          {/* Team Members Tab */}
          {activeTab === 'team-members' && (
            <div className="max-w-6xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-8 duration-700">
              <TeamMembersTab role="sourcing_partner" />
            </div>
          )}


          {activeTab === 'settings' && (
            <div className="max-w-5xl mx-auto space-y-12 animate-in fade-in slide-in-from-bottom-8 duration-700">
              <div className="border-b border-slate-200 pb-6">
                <h2 className="text-2xl font-bold text-slate-800 tracking-tight">Settings</h2>
                <p className="text-slate-500 mt-0.5 uppercase text-[9px] font-bold tracking-widest opacity-70">Account Settings</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="group bg-white rounded-2xl p-8 border border-slate-200 hover:border-blue-400 transition-all duration-300 shadow-sm relative overflow-hidden">
                  <div className="h-12 w-12 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 mb-6 group-hover:scale-110 transition-transform">
                    <User className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2 uppercase tracking-tight">{t('dashboard.myProfile') || 'My Profile'}</h3>
                  <p className="text-slate-500 text-[10px] mb-8 leading-relaxed font-bold uppercase tracking-widest opacity-70">Update your company and contact information.</p>
                  <button
                    onClick={() => { navigate('/sourcing/my-profile'); setIsEditingProfile(true); }}
                    className="w-full py-3 bg-slate-50 hover:bg-blue-600 text-slate-600 hover:text-white font-bold rounded-xl border border-slate-200 hover:border-blue-600 transition-all text-[9px] uppercase tracking-widest shadow-sm"
                  >
                    Edit Profile Details
                  </button>
                </div>

                <div className="group bg-white rounded-2xl p-8 border border-slate-200 hover:border-blue-400 transition-all duration-300 shadow-sm relative overflow-hidden">
                  <div className="h-12 w-12 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 mb-6 group-hover:scale-110 transition-transform">
                    <Key className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2 uppercase tracking-tight">{t('dashboard.resetPassword') || 'Password'}</h3>
                  <p className="text-slate-500 text-[10px] mb-8 leading-relaxed font-bold uppercase tracking-widest opacity-70">Change your account password.</p>
                  <button
                    onClick={() => setShowPasswordModal(true)}
                    className="w-full py-3 bg-slate-50 hover:bg-blue-600 text-slate-600 hover:text-white font-bold rounded-xl border border-slate-200 hover:border-blue-600 transition-all text-[9px] uppercase tracking-widest shadow-sm"
                  >
                    Reset Password
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {
        showCandidateModal && (
          <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-in fade-in duration-300">
            <div className="bg-white border border-slate-200 rounded-[32px] shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-hidden transform scale-100 transition-all flex flex-col relative">
              <div className="flex justify-between items-center p-6 border-b border-slate-100 sticky top-0 bg-white/80 backdrop-blur-md z-20">
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 tracking-tight">Register New Candidate</h3>
                  <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1 opacity-70">
                    Add a new candidate to your pipeline
                  </p>
                </div>
                <button
                  onClick={() => setShowCandidateModal(false)}
                  className="h-10 w-10 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-all group"
                >
                  <X className="h-6 w-6 group-hover:rotate-90 transition-transform duration-300" />
                </button>
              </div>

              <div className="p-8 overflow-y-auto flex-grow scrollbar-hide">
                <form onSubmit={(e) => { e.preventDefault(); handleSaveCandidate(); }} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Full Name *</label>
                      <div className="relative group">
                        <User className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                        <input
                          type="text"
                          required
                          value={candidateForm.name}
                          onChange={(e) => setCandidateForm({ ...candidateForm, name: e.target.value })}
                          className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all placeholder:text-slate-400 text-sm"
                          placeholder="e.g. John Doe"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Email Address *</label>
                      <div className="relative group">
                        <Mail className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                        <input
                          type="email"
                          required
                          value={candidateForm.email}
                          onChange={(e) => setCandidateForm({ ...candidateForm, email: e.target.value })}
                          className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all placeholder:text-slate-400 text-sm"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Phone Number *</label>
                      <div className="relative group">
                        <Phone className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                        <input
                          type="tel"
                          required
                          value={candidateForm.phone}
                          onChange={(e) => setCandidateForm({ ...candidateForm, phone: e.target.value })}
                          className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all placeholder:text-slate-400 text-sm"
                          placeholder="+1 234 567 890"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Password {editingCandidate ? '(Leave blank to keep current)' : '*'}</label>
                      <div className="relative group">
                        <Lock className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                        <input
                          type="password"
                          required={!editingCandidate}
                          value={candidateForm.password}
                          onChange={(e) => setCandidateForm({ ...candidateForm, password: e.target.value })}
                          className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all placeholder:text-slate-400 text-sm"
                          placeholder="••••••••"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Application Stage</label>
                      <div className="relative group">
                        <Activity className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                        <select
                          value={candidateForm.progress_stage}
                          onChange={(e) => setCandidateForm({ ...candidateForm, progress_stage: e.target.value })}
                          className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all text-sm appearance-none"
                        >
                          <option value="Language Learning">Language Learning</option>
                          <option value="Document Submitted">Document Submitted</option>
                          <option value="Screening Waiting">Screening Waiting</option>
                          <option value="Screening Passed">Screening Passed</option>
                          <option value="Interview Waiting">Interview Waiting</option>
                          <option value="Interview Scheduled">Interview Scheduled</option>
                          <option value="Interview attended">Interview attended</option>
                          <option value="Offer Received">Offer Received</option>
                          <option value="Visa submitted">Visa submitted</option>
                          <option value="Visa Granted">Visa Granted</option>
                          <option value="Migrated">Migrated</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4 pt-6 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={() => setShowCandidateModal(false)}
                      className="flex-1 px-6 py-3 bg-white border border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50 transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-6 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-500/20 transition-all"
                    >
                      {editingCandidate ? 'Update Candidate' : 'Register Candidate'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }

      {/* Pool Modal */}
      {
        showPoolModal && (
          <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-in fade-in duration-300">
            <div className="bg-white border border-slate-200 rounded-3xl shadow-2xl w-full max-w-md transform scale-100 transition-all relative overflow-hidden">
              <div className="flex justify-between items-center p-6 border-b border-slate-50">
                <h3 className="text-xl font-bold text-slate-800 tracking-tight">Create Talent Pool</h3>
                <button
                  onClick={() => setShowPoolModal(false)}
                  className="h-9 w-9 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Pool Name</label>
                  <div className="relative group">
                    <Folder className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                    <input
                      type="text"
                      value={poolForm.name}
                      onChange={(e) => setPoolForm({ ...poolForm, name: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all placeholder:text-slate-400 text-sm"
                      placeholder="e.g. Registered Nurses (B2 German)"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Description</label>
                  <textarea
                    value={poolForm.description}
                    onChange={(e) => setPoolForm({ ...poolForm, description: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all min-h-[120px] resize-none placeholder:text-slate-400 text-sm"
                    placeholder="Describe this talent pool..."
                  />
                </div>

                <div className="flex items-center gap-3 mt-4 p-4 bg-slate-50 border border-slate-200 rounded-xl">
                  <input
                    type="checkbox"
                    id="sourcingPoolPublicToggle"
                    checked={poolForm.is_public}
                    onChange={(e) => setPoolForm({ ...poolForm, is_public: e.target.checked })}
                    className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500/20"
                  />
                  <label htmlFor="sourcingPoolPublicToggle" className="cursor-pointer flex flex-col items-start leading-none">
                    <span className="block text-sm font-bold text-slate-800 mb-1">Make Pool Public</span>
                    <span className="block text-xs text-slate-500">Share this pool with Polyglots Staff</span>
                  </label>
                </div>
              </div>

              <div className="p-6 flex gap-3 border-t border-slate-50 mt-2">
                <button
                  onClick={() => setShowPoolModal(false)}
                  className="flex-1 py-3 bg-slate-100 text-slate-500 font-bold rounded-xl hover:bg-slate-200 transition-all uppercase tracking-widest text-[10px]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreatePool}
                  className="flex-[2] py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 uppercase tracking-widest text-[10px]"
                >
                  Create Pool
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Manage Pool Modal */}
      {
        showAddToPoolModal && selectedPool && (
          <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm flex items-center justify-center z-[110] p-4 animate-in fade-in duration-300">
            <div className="bg-white border border-slate-200 rounded-[32px] shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden transform scale-100 transition-all flex flex-col">
              <div className="flex justify-between items-center p-8 border-b border-slate-100">
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 tracking-tight">Manage Pool</h3>
                  <p className="text-[10px] text-blue-600 font-bold uppercase tracking-widest mt-1 italic flex items-center gap-2">
                    <span className="w-4 h-px bg-blue-500" /> Pool: {selectedPool.name}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowAddToPoolModal(false);
                    setSelectedPool(null);
                    setSelectedCandidatesForPool([]);
                  }}
                  className="h-9 w-9 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="flex px-8 border-b border-slate-100 bg-slate-50/50">
                <button
                  onClick={() => setModalTab('members')}
                  className={`flex-1 py-4 text-[10px] font-bold uppercase tracking-widest transition-all border-b-2 ${modalTab === 'members'
                    ? 'border-blue-600 text-blue-600 bg-blue-600/5'
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`}
                >
                  Pool Members ({poolCandidates?.length || 0})
                </button>
                <button
                  onClick={() => setModalTab('add')}
                  className={`flex-1 py-4 text-[10px] font-bold uppercase tracking-widest transition-all border-b-2 ${modalTab === 'add'
                    ? 'border-blue-600 text-blue-600 bg-blue-600/5'
                    : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`}
                >
                  Add Candidates
                </button>
              </div>

              <div className="p-8 overflow-y-auto flex-grow scrollbar-hide">
                {modalTab === 'members' ? (
                  <div className="space-y-3">
                    {poolCandidates && poolCandidates.length > 0 ? (
                      poolCandidates.map((candidate) => (
                        <div key={candidate.id} className="group flex justify-between items-center p-4 rounded-2xl border border-slate-100 bg-white hover:bg-slate-50 hover:border-slate-200 transition-all duration-300 shadow-sm">
                          <div className="flex items-center gap-4">
                            <div className="h-10 w-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 font-bold text-base shadow-sm group-hover:scale-105 transition-transform">
                              {candidate.name.charAt(0)}
                            </div>
                            <div>
                              <p className="text-sm font-bold text-slate-800">{candidate.name}</p>
                              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">{candidate.occupation || 'Candidate'}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleRemoveCandidateFromPool(candidate.id)}
                            className="h-8 w-8 flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-16 bg-slate-50 border-2 border-dashed border-slate-100 rounded-3xl">
                        <Users className="h-10 w-10 mx-auto text-slate-200 mb-4" />
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">No candidates in this pool yet</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {candidates
                      .filter(c => !poolCandidates?.some(pc => pc.id === c.id))
                      .map((candidate) => (
                        <div
                          key={candidate.id}
                          onClick={() => {
                            if (selectedCandidatesForPool.includes(candidate.id)) {
                              setSelectedCandidatesForPool(prev => prev.filter(id => id !== candidate.id));
                            } else {
                              setSelectedCandidatesForPool(prev => [...prev, candidate.id]);
                            }
                          }}
                          className={`group flex items-center p-4 rounded-2xl border cursor-pointer transition-all duration-300 ${selectedCandidatesForPool.includes(candidate.id)
                            ? 'border-blue-500 bg-blue-50 shadow-sm'
                            : 'border-slate-100 bg-white hover:bg-slate-50 hover:border-slate-200'
                            }`}
                        >
                          <div className={`h-5 w-5 rounded-md border-2 flex items-center justify-center mr-4 transition-all ${selectedCandidatesForPool.includes(candidate.id) ? 'bg-blue-600 border-blue-600' : 'border-slate-200 bg-white'
                            }`}>
                            {selectedCandidatesForPool.includes(candidate.id) && <CheckCircle className="h-3.5 w-3.5 text-white" />}
                          </div>
                          <div className="flex items-center gap-4">
                            <div className={`h-10 w-10 rounded-xl border flex items-center justify-center font-bold text-base transition-all ${selectedCandidatesForPool.includes(candidate.id) ? 'bg-blue-100 border-blue-200 text-blue-600' : 'bg-slate-50 border-slate-100 text-slate-400'}`}>
                              {candidate.name.charAt(0)}
                            </div>
                            <div>
                              <p className={`text-sm font-bold transition-colors ${selectedCandidatesForPool.includes(candidate.id) ? 'text-slate-800' : 'text-slate-600'}`}>{candidate.name}</p>
                              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">{candidate.email}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              <div className="p-8 border-t border-slate-100 bg-slate-50/50 flex justify-between items-center">
                {modalTab === 'add' ? (
                  <>
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest italic">Selected</span>
                      <span className="text-sm font-bold text-blue-600 mt-0.5">
                        {selectedCandidatesForPool.length} candidates selected
                      </span>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          setShowAddToPoolModal(false);
                          setSelectedPool(null);
                          setSelectedCandidatesForPool([]);
                        }}
                        className="px-6 py-2.5 bg-slate-100 text-slate-500 font-bold rounded-xl hover:bg-slate-200 transition-all uppercase tracking-widest text-[10px]"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddCandidatesToPool}
                        disabled={selectedCandidatesForPool.length === 0}
                        className="px-8 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 disabled:opacity-30 transition-all shadow-lg shadow-blue-600/20 uppercase tracking-widest text-[10px]"
                      >
                        Add Selected
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setShowAddToPoolModal(false);
                      setSelectedPool(null);
                      setSelectedCandidatesForPool([]);
                    }}
                    className="w-full py-3 bg-slate-100 text-slate-500 font-bold rounded-xl hover:bg-slate-200 transition-all uppercase tracking-widest text-xs"
                  >
                    Close
                  </button>
                )}
              </div>
            </div>
          </div>
        )
      }

      {/* Password Reset Modal */}
      {
        showPasswordModal && (
          <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm flex items-center justify-center z-[120] p-4 animate-in fade-in duration-300">
            <div className="bg-white border border-slate-200 rounded-3xl shadow-2xl w-full max-w-md transform scale-100 transition-all relative overflow-hidden">
              <div className="flex justify-between items-center p-6 border-b border-slate-50">
                <h3 className="text-xl font-bold text-slate-800 tracking-tight">Reset Password</h3>
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="h-9 w-9 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-6 space-y-5">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">New Password</label>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                    placeholder="••••••••"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest pl-1">Confirm Password</label>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
                    placeholder="••••••••"
                  />
                </div>
                {passwordError && <p className="text-red-500 text-[10px] font-bold uppercase tracking-widest pl-1">{passwordError}</p>}
              </div>

              <div className="p-6 flex gap-3 border-t border-slate-50 mt-2">
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="flex-1 py-3 bg-slate-100 text-slate-500 font-bold rounded-xl hover:bg-slate-200 transition-all uppercase tracking-widest text-[10px]"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePasswordReset}
                  className="flex-[2] py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20 uppercase tracking-widest text-[10px]"
                >
                  Update Password
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Feedback Modal */}
      {
        showFeedbackModal && selectedFeedback && (
          <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm flex items-center justify-center z-[120] p-4 animate-in fade-in duration-300">
            <div className="bg-white border border-slate-200 rounded-[32px] shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden transform scale-100 transition-all flex flex-col">
              <div className="flex justify-between items-center p-8 border-b border-slate-100 sticky top-0 bg-white/80 backdrop-blur-md z-10">
                <div>
                  <h3 className="text-2xl font-bold text-slate-800 tracking-tight">Feedback Details</h3>
                  <div className="flex items-center gap-4 mt-1">
                    <span className={`px-2 py-0.5 text-[9px] font-bold rounded-md uppercase tracking-widest ${selectedFeedback.feedback_type === 'request' ? 'bg-indigo-50 text-indigo-600' : 'bg-blue-50 text-blue-600'
                      }`}>
                      {selectedFeedback.feedback_type}
                    </span>
                    <span className="text-slate-200 font-bold">•</span>
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest italic">{new Date(selectedFeedback.created_at).toLocaleString()}</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowFeedbackModal(false);
                    setSelectedFeedback(null);
                    setFeedbackResponses([]);
                  }}
                  className="h-10 w-10 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-all"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="p-8 space-y-8 overflow-y-auto scrollbar-hide">
                <div className="bg-slate-50 rounded-3xl p-6 border border-slate-100">
                  <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">Original Message</h4>
                  <p className="text-slate-800 text-lg font-bold leading-tight uppercase tracking-tight italic">{selectedFeedback.message}</p>

                  <div className="flex items-center gap-8 mt-6 pt-6 border-t border-slate-200/50">
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Priority</span>
                      <p className={`text-[10px] font-bold mt-1 uppercase tracking-widest ${selectedFeedback.priority === 'urgent' ? 'text-red-500' : 'text-slate-500'
                        }`}>{selectedFeedback.priority}</p>
                    </div>
                    <div>
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Status</span>
                      <p className="text-[10px] font-bold text-blue-600 mt-1 uppercase tracking-widest italic">{selectedFeedback.status}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <h4 className="flex items-center gap-2 text-xs font-bold text-slate-800 uppercase tracking-widest">
                    <MessageSquare className="h-4 w-4 text-blue-500" />
                    <span>Conversation</span>
                  </h4>

                  <div className="space-y-4">
                    {/* Original Message */}
                    <div className="flex gap-4">
                      <div className="h-10 w-10 rounded-xl bg-blue-100 border border-blue-200 flex items-center justify-center text-blue-600 font-bold text-xs shadow-sm">
                        ME
                      </div>
                      <div className="flex-1 bg-white border border-slate-100 rounded-[20px] rounded-tl-none p-4 shadow-sm">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest italic">You</span>
                          <span className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">{new Date(selectedFeedback.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-slate-600 text-sm font-medium leading-relaxed">{selectedFeedback.message}</p>
                      </div>
                    </div>

                    {feedbackResponses.map((response) => (
                      <div key={response.id} className="flex gap-4">
                        <div className={`h-10 w-10 rounded-xl flex items-center justify-center text-white font-bold text-xs shadow-sm ${response.responder_role === 'sourcing_partner' ? 'bg-blue-600' : 'bg-slate-700'
                          }`}>
                          {response.responder_name?.charAt(0)}
                        </div>
                        <div className={`flex-1 border rounded-[20px] rounded-tl-none p-4 shadow-sm ${response.responder_role === 'sourcing_partner' ? 'bg-blue-50 border-blue-100' : 'bg-slate-50 border-slate-100'
                          }`}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-[10px] font-bold text-slate-800 uppercase tracking-widest italic">{response.responder_name}</span>
                            <span className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">{new Date(response.created_at).toLocaleString()}</span>
                          </div>
                          <p className="text-slate-600 text-sm font-medium leading-relaxed">{response.response_text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {selectedFeedback.status !== 'resolved' ? (
                  <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 space-y-4 shadow-sm">
                    <div className="flex justify-between items-center">
                      <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest italic pl-1">Reply</h5>
                      <button
                        onClick={handleMarkFeedbackResolved}
                        className="text-[9px] font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1.5 transition-colors uppercase tracking-widest"
                      >
                        <CheckCircle className="h-3.5 w-3.5" /> Mark as Resolved
                      </button>
                    </div>
                    <textarea
                      value={responseText}
                      onChange={(e) => setResponseText(e.target.value)}
                      placeholder="Type your reply..."
                      className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 min-h-[100px] resize-none text-sm transition-all placeholder:text-slate-300"
                    />
                    <div className="flex justify-end">
                      <button
                        onClick={handleSendResponse}
                        disabled={!responseText.trim() || isSendingResponse}
                        className="px-6 py-2.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 disabled:opacity-30 transition-all shadow-md shadow-blue-600/10 flex items-center gap-2 uppercase tracking-widest text-[10px]"
                      >
                        {isSendingResponse ? (
                          <div className="h-3.5 w-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                          <Send className="h-3.5 w-3.5" />
                        )}
                        Send Reply
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-emerald-50 border border-emerald-100 rounded-3xl p-8 text-center shadow-sm">
                    <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 mx-auto mb-4 shadow-sm">
                      <CheckCircle className="h-6 w-6" />
                    </div>
                    <p className="text-base font-bold text-slate-800 uppercase tracking-tight italic">Resolved</p>
                    <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-widest mt-1">This feedback has been resolved</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

    </div >
  );
};

export default SourcingPartnerDashboard;
